const PRODUCT_COLORS = ['#95e0ad', '#f8dd74', '#bfd9ef', '#ffb18e', '#cdc2ff']
const formatDecimal = (value, digits = 1) => {
	return Number(value || 0).toLocaleString('pt-BR', {
		minimumFractionDigits: digits,
		maximumFractionDigits: digits,
	})
}

const getInitials = (value = '') => {
	const parts = String(value).trim().split(/\s+/).filter(Boolean)

	if (!parts.length) {
		return 'KS'
	}

	return parts.slice(0, 2).map((part) => part[0]).join('').toUpperCase()
}

const sanitizePointId = (value = '') => String(value).trim().toLowerCase().replace(/[^a-z0-9]+/g, '-') || 'item'

const splitCamelCase = (value = '') => {
	return String(value)
		.replace(/([a-z0-9])([A-Z])/g, '$1 $2')
		.trim()
}

const buildProductNameLines = (value = '') => {
	const normalized = splitCamelCase(value)
	const words = normalized.split(/\s+/).filter(Boolean)

	if (!words.length) {
		return ['Produto']
	}

	if (words.length === 1) {
		const word = words[0]

		if (word.length <= 10) {
			return [word]
		}

		const midpoint = Math.ceil(word.length / 2)
		return [word.slice(0, midpoint), word.slice(midpoint)]
	}

	const firstLine = words[0]
	const secondLine = words.slice(1).join(' ')

	if (secondLine.length <= 12) {
		return [firstLine, secondLine]
	}

	return [firstLine, `${secondLine.slice(0, 11)}...`]
}

const getProductNameTone = (lines = []) => {
	const joinedLength = lines.join('').length

	if (lines.length > 1 || joinedLength > 12) {
		return 'compact'
	}

	return 'regular'
}

const getAccountPointColor = (selectedAccount) => {
	return '#8f9eab'
}

const getAccountColor = (account, index = 0) => {
	return '#8f9eab'
}

const getTone = (sentiment) => {
	if (typeof sentiment !== 'number' || sentiment < 0) {
		return 'neutral'
	}

	if (sentiment >= 7) {
		return 'positive'
	}

	if (sentiment >= 5) {
		return 'neutral'
	}

	return 'negative'
}

const getVolumeMeta = (count) => {
	if (count >= 10) {
		return {
			volumeLabel: 'Alto',
			volumeClassName: 'scout-graphics-volume scout-graphics-volume--high',
		}
	}

	if (count >= 4) {
		return {
			volumeLabel: 'Médio',
			volumeClassName: 'scout-graphics-volume scout-graphics-volume--medium',
		}
	}

	return {
		volumeLabel: 'Baixo',
		volumeClassName: 'scout-graphics-volume scout-graphics-volume--low',
	}
}

const formatCountLabel = (value) => {
	return Number(value || 0).toLocaleString('pt-BR', {
		maximumFractionDigits: 0,
	})
}

const getNiceTickStep = (maxValue) => {
	const safeMaxValue = Math.max(1, Number(maxValue || 0))
	const rawStep = safeMaxValue / 3
	const magnitude = 10 ** Math.floor(Math.log10(rawStep || 1))
	const normalized = rawStep / magnitude
	const candidates = [1, 2, 3, 4, 5, 6, 8, 10]
	const niceBase = candidates.find((candidate) => normalized <= candidate) || 10

	return niceBase * magnitude
}

const buildProductTicks = (items = []) => {
	const maxValue = items.reduce((highest, item) => Math.max(highest, Number(item?.value || 0)), 0)

	if (maxValue <= 0) {
		return [0, 1, 2, 3]
	}

	const step = getNiceTickStep(maxValue)

	return [0, step, step * 2, step * 3]
}

const buildAccountLookup = (accounts = []) => {
	return (Array.isArray(accounts) ? accounts : []).reduce((lookup, account, index) => {
		lookup[String(account.id)] = {
			id: account.id,
			name: account.name,
			initials: getInitials(account.name),
			avatarUrl: account.avatarUrl || null,
			color: getAccountColor(account, index),
		}

		return lookup
	}, {})
}

const buildProductEntries = (counts = {}, productBreakdown = [], accountLookup = {}, showAccountBadges = true) => {
	const entries = Object.entries(counts)
		.filter(([, count]) => Number(count) > 0)
		.sort((first, second) => Number(second[1] || 0) - Number(first[1] || 0))

	const breakdownLookup = (Array.isArray(productBreakdown) ? productBreakdown : []).reduce((lookup, item) => {
		lookup[String(item?.name || '')] = Array.isArray(item?.accounts) ? item.accounts : []
		return lookup
	}, {})

	const total = entries.reduce((sum, [, count]) => sum + Number(count || 0), 0)

	const items = entries.map(([name, count], index) => {
		const numericCount = Number(count || 0)
		const share = total > 0 ? (numericCount / total) * 100 : 0
		const volumeMeta = getVolumeMeta(numericCount)
		const nameLines = buildProductNameLines(name)
		const accountBadges = showAccountBadges
			? (breakdownLookup[String(name)] || [])
				.sort((first, second) => Number(second?.count || 0) - Number(first?.count || 0))
				.slice(0, 3)
				.map((accountEntry, badgeIndex) => {
					const accountMeta = accountLookup[String(accountEntry?.account_id)] || {}

					return {
						id: `${sanitizePointId(name)}-${accountEntry?.account_id || badgeIndex}`,
						name: accountMeta.name || accountEntry?.account_name || 'Conta',
						initials: accountMeta.initials || getInitials(accountEntry?.account_name),
						avatarUrl: accountMeta.avatarUrl || null,
						count: Number(accountEntry?.count || 0),
						label: formatCountLabel(accountEntry?.count || 0),
						color: accountMeta.color || '#8f9eab',
						stackIndex: badgeIndex,
					}
				})
			: []

		return {
			id: `${name}-${index}`,
			name,
			value: numericCount,
			count: numericCount,
			share,
			shareLabel: `${Math.round(share)}%`,
			nameLines,
			nameTone: getProductNameTone(nameLines),
			color: PRODUCT_COLORS[index % PRODUCT_COLORS.length],
			x: [28, 60, 79, 46, 70][index] || ((index + 1) / (entries.length + 1)) * 100,
			accountBadges,
			...volumeMeta,
		}
	})

	return {
		total,
		ticks: buildProductTicks(items),
		items,
	}
}

const buildSummaryItems = (summaries = []) => {
	return summaries
		.filter((item) => item?.summary)
		.slice(0, 6)
		.map((item, index) => ({
			id: item.id || `summary-${index}`,
			name: item.name || 'Conversa monitorada',
			summary: item.summary,
			tone: getTone(Number(item.sentiment)),
			sentiment: typeof item.sentiment === 'number' ? item.sentiment : null,
		}))
}

const buildSatisfactionPoints = (average, selectedAccount = null, accountSentiment = [], accountLookup = {}) => {
	if (selectedAccount) {
		if (typeof average !== 'number' || average < 0) {
			return []
		}

		return [{
			id: `account-average-${selectedAccount.id}`,
			name: selectedAccount.name,
			value: Number(average),
			label: formatDecimal(average),
			initials: getInitials(selectedAccount.name),
			avatarUrl: selectedAccount.avatarUrl || null,
			color: getAccountPointColor(selectedAccount),
			x: 58,
		}]
	}

	return (Array.isArray(accountSentiment) ? accountSentiment : [])
		.filter((item) => typeof item?.sentiment === 'number' && item.sentiment >= 0)
		.sort((first, second) => Number(second?.sentiment || 0) - Number(first?.sentiment || 0))
		.slice(0, 4)
		.map((item, index, items) => {
			const accountMeta = accountLookup[String(item?.account_id)] || {}

			return {
				id: `account-average-${item?.account_id || index}`,
				name: accountMeta.name || item?.account_name || 'Conta',
				value: Number(item.sentiment),
				label: formatDecimal(item.sentiment),
				initials: accountMeta.initials || getInitials(item?.account_name),
				avatarUrl: accountMeta.avatarUrl || null,
				color: accountMeta.color || getAccountPointColor(null),
				x: [40, 56, 70, 28][index] || ((index + 1) / (items.length + 1)) * 100,
			}
		})
}

const EMPTY_MODEL = {
	hasData: false,
	summaries: [],
	products: {
		items: [],
		totalMentions: 0,
		trend: null,
		ticks: [0, 1, 2, 3],
		tableRows: [],
	},
	satisfaction: {
		average: null,
		averageLabel: '0,0',
		trend: null,
		points: [],
		ticks: [
			{ label: 'Perfeito', displayValue: '10', value: 10, color: '#16a34a' },
			{ label: 'Bom', displayValue: '6,0', value: 6, color: '#d89b11' },
			{ label: 'Ruim', displayValue: '3,0', value: 3, color: '#e3342f' },
			{ label: 'Péssimo', displayValue: '0,0', value: 0, color: '#6b1a22' },
		],
	},
}

const MOCK_MODEL = {
	hasData: true,
	summaries: [
		{
			id: 'summary-1',
			name: 'Maria Santos',
			summary: 'Maria deseja receber carta de quitação de débitos',
			tone: 'neutral',
		},
		{
			id: 'summary-2',
			name: 'Maria Santos',
			summary: 'Maria deseja receber carta de quitação de débitos',
			tone: 'negative',
		},
		{
			id: 'summary-3',
			name: 'Gerentes de São Paulo',
			summary: 'Ricardo e Felipe relataram alto número de conversões no quarter',
			tone: 'positive',
		},
		{
			id: 'summary-4',
			name: 'Projeto Alfa',
			summary: 'Maria Clara relatou reuniões positivas no dia de ontem e todos celebraram com ela as metas alcançadas.',
			tone: 'positive',
		},
	],
	products: {
		items: [
			{ id: 'alfa', name: 'Alfa', value: 12, count: 12, share: 63.2, shareLabel: '63%', color: '#95e0ad', x: 24, volumeLabel: 'Alto', volumeClassName: 'scout-graphics-volume scout-graphics-volume--high' },
			{ id: 'beta', name: 'Beta', value: 5, count: 5, share: 26.3, shareLabel: '26%', color: '#f8dd74', x: 61, volumeLabel: 'Médio', volumeClassName: 'scout-graphics-volume scout-graphics-volume--medium' },
			{ id: 'delta', name: 'Delta', value: 2, count: 2, share: 10.5, shareLabel: '11%', color: '#bfd9ef', x: 86, volumeLabel: 'Baixo', volumeClassName: 'scout-graphics-volume scout-graphics-volume--low' },
		],
		totalMentions: 19,
		trend: { value: '8,3%', direction: 'up' },
		ticks: [0, 4, 8, 12],
		tableRows: [
			{ id: 'alfa-table', name: 'Alfa', count: 12, shareLabel: '63%', color: '#95e0ad', volumeLabel: 'Alto', volumeClassName: 'scout-graphics-volume scout-graphics-volume--high' },
			{ id: 'beta-table', name: 'Beta', count: 5, shareLabel: '26%', color: '#f8dd74', volumeLabel: 'Médio', volumeClassName: 'scout-graphics-volume scout-graphics-volume--medium' },
			{ id: 'delta-table', name: 'Delta', count: 2, shareLabel: '11%', color: '#bfd9ef', volumeLabel: 'Baixo', volumeClassName: 'scout-graphics-volume scout-graphics-volume--low' },
		],
	},
	satisfaction: {
		average: 7.75,
		averageLabel: '7,75',
		trend: { value: '6,5%', direction: 'up' },
		points: [
			{ id: 'joao', name: 'João da Silva', value: 8.5, label: '8,5', initials: 'JS', color: '#2ab35b', x: 40 },
			{ id: 'luciana', name: 'Luciana Oliveira', value: 7.0, label: '7,0', initials: 'LO', color: '#ff5a5a', x: 56 },
		],
		ticks: [
			{ label: 'Perfeito', displayValue: '10', value: 10, color: '#16a34a' },
			{ label: 'Bom', displayValue: '6,0', value: 6, color: '#d89b11' },
			{ label: 'Ruim', displayValue: '3,0', value: 3, color: '#e3342f' },
			{ label: 'Péssimo', displayValue: '0,0', value: 0, color: '#6b1a22' },
		],
	},
}

const hasLiveAnalytics = (analytics) => {
	if (!analytics) {
		return false
	}

	if (Array.isArray(analytics?.summaries) && analytics.summaries.length) {
		return true
	}

	if (typeof analytics?.sentiment === 'number' && analytics.sentiment >= 0) {
		return true
	}

	return Boolean(analytics?.products_count?.[0] && Object.keys(analytics.products_count[0]).length)
}

export const createHomeGraphicsViewModel = ({ analytics, selectedAccount = null, useMockData = true } = {}) => {
	if (useMockData) {
		return MOCK_MODEL
	}

	if (!hasLiveAnalytics(analytics)) {
		return EMPTY_MODEL
	}

	const summaryItems = buildSummaryItems(analytics?.summaries || [])
	const accountLookup = buildAccountLookup(analytics?.accounts || [])
	const productEntries = buildProductEntries(analytics?.products_count?.[0] || {}, analytics?.product_breakdown || [], accountLookup, !selectedAccount)
	const average = typeof analytics?.sentiment === 'number' && analytics.sentiment >= 0 ? Number(analytics.sentiment) : null
	const satisfactionPoints = buildSatisfactionPoints(average, selectedAccount, analytics?.account_sentiment || [], accountLookup)

	return {
		hasData: Boolean(summaryItems.length || productEntries.items.length || average !== null),
		summaries: summaryItems,
		products: {
			items: productEntries.items,
			totalMentions: productEntries.total,
			trend: null,
			ticks: productEntries.ticks,
			tableRows: productEntries.items.map((item) => ({
				id: `${item.id}-row`,
				name: item.name,
				count: item.count,
				shareLabel: item.shareLabel,
				countLabel: formatCountLabel(item.count),
				color: item.color,
				volumeLabel: item.volumeLabel,
				volumeClassName: item.volumeClassName,
			})),
		},
		satisfaction: {
			average,
			averageLabel: average !== null ? formatDecimal(average) : '0,0',
			trend: null,
			points: satisfactionPoints,
			ticks: EMPTY_MODEL.satisfaction.ticks,
		},
	}
}